/**
 * Hotspot story — pins over an image act as tabs that swap the panel of theme
 * blocks beside it.
 *
 * The pins are server-rendered by blocks/hotspot-story.liquid; each panel is a
 * `_hotspot-story-item` block that carries its own position in data attributes.
 * `syncPins()` reconciles the two, which covers both the theme editor
 * re-rendering a single hotspot block and any case where the pin markup is
 * missing entirely.
 */
/**
 * Coerces a `data-x` / `data-y` attribute to a usable percentage, keeping 0 but
 * falling back to centre for missing or non-numeric values.
 *
 * @param {string | undefined} value
 * @returns {number}
 */
function percent(value) {
  const parsed = Number.parseFloat(value ?? '');

  return Number.isFinite(parsed) ? parsed : 50;
}

class HotspotStoryComponent extends HTMLElement {
  connectedCallback() {
    this.pinList = this.querySelector('.hotspot-story__pins');
    this.panelList = this.querySelector('.hotspot-story__panels');

    if (!this.pinList || !this.panelList) return;

    this.syncPins();
    this.activate(this.panels[0]?.dataset.hotspotId, { focus: false });

    this.pinList.addEventListener('click', this.handleClick);
    this.pinList.addEventListener('keydown', this.handleKeydown);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      // The editor can re-render a single hotspot block without touching this
      // element, so watch the panels for position/label edits and re-sync.
      this.observer = new MutationObserver(() => this.syncPins());
      this.observer.observe(this.panelList, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-x', 'data-y', 'data-label'],
      });
    }
  }

  disconnectedCallback() {
    this.pinList?.removeEventListener('click', this.handleClick);
    this.pinList?.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return Array.from(this.panelList?.querySelectorAll(':scope > .hotspot-story__panel') ?? []);
  }

  /** @returns {HTMLElement[]} */
  get pins() {
    return Array.from(this.pinList?.querySelectorAll(':scope > .hotspot-story__pin') ?? []);
  }

  /**
   * Rebuilds the pin list from the panels so a pin exists, in the right place,
   * for every hotspot — even after the editor re-renders one block in isolation.
   */
  syncPins() {
    const numbered = this.classList.contains('hotspot-story--pin-numbered');
    const existing = new Map(this.pins.map((pin) => [pin.dataset.hotspotId, pin]));

    this.panels.forEach((panel, index) => {
      const id = panel.dataset.hotspotId;
      if (!id) return;

      const pin = existing.get(id) ?? this.createPin(id);
      existing.delete(id);

      const label = panel.dataset.label || String(index + 1);
      const marker = pin.querySelector('.hotspot-story__pin-marker');

      if (marker) marker.textContent = numbered ? label : '';
      pin.setAttribute('aria-label', panel.dataset.label || `Hotspot ${index + 1}`);
      pin.style.setProperty('--hotspot-story-pin-x', `${percent(panel.dataset.x)}%`);
      pin.style.setProperty('--hotspot-story-pin-y', `${percent(panel.dataset.y)}%`);

      // Keep DOM order aligned with panel order so arrow keys read naturally.
      if (this.pinList.children[index] !== pin) {
        this.pinList.insertBefore(pin, this.pinList.children[index] ?? null);
      }
    });

    // Any pin left over belongs to a hotspot that no longer exists.
    existing.forEach((pin) => pin.remove());
  }

  /**
   * @param {string} id
   * @returns {HTMLElement}
   */
  createPin(id) {
    const pin = document.createElement('button');

    pin.type = 'button';
    pin.className = 'hotspot-story__pin';
    pin.dataset.hotspotId = id;
    pin.setAttribute('role', 'tab');
    pin.id = `hotspot-story-tab-${id}`;
    pin.setAttribute('aria-controls', `hotspot-story-panel-${id}`);
    pin.setAttribute('aria-selected', 'false');
    pin.tabIndex = -1;

    const marker = document.createElement('span');
    marker.className = 'hotspot-story__pin-marker';
    marker.setAttribute('aria-hidden', 'true');
    pin.append(marker);

    return pin;
  }

  /**
   * @param {string | undefined} id
   * @param {{ focus?: boolean }} [options]
   */
  activate(id, { focus = true } = {}) {
    if (!id) return;

    this.panels.forEach((panel) => {
      panel.toggleAttribute('data-active', panel.dataset.hotspotId === id);
      // Inactive panels are visually hidden, so keep them out of the a11y tree
      // and off the tab sequence too.
      panel.toggleAttribute('inert', panel.dataset.hotspotId !== id);
    });

    this.pins.forEach((pin) => {
      const selected = pin.dataset.hotspotId === id;

      pin.setAttribute('aria-selected', String(selected));
      pin.tabIndex = selected ? 0 : -1;

      if (selected && focus) pin.focus();
    });
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const pin = event.target instanceof Element ? event.target.closest('.hotspot-story__pin') : null;

    if (pin) this.activate(pin.dataset.hotspotId, { focus: false });
  };

  /**
   * Standard tablist keyboard support: arrows move between hotspots,
   * Home/End jump to the ends.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    const pins = this.pins;
    const current = pins.findIndex((pin) => pin.getAttribute('aria-selected') === 'true');

    if (pins.length === 0) return;

    let next = current;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % pins.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + pins.length) % pins.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = pins.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(pins[next]?.dataset.hotspotId);
  };

  /**
   * In the theme editor, selecting a hotspot — or any block nested inside one —
   * reveals that hotspot's panel.
   *
   * @param {Event} event
   */
  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    this.syncPins();

    const panel = event.target.closest('.hotspot-story__panel');

    if (panel) this.activate(panel.dataset.hotspotId, { focus: false });
  };
}

if (!customElements.get('hotspot-story-component')) {
  customElements.define('hotspot-story-component', HotspotStoryComponent);
}
